import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

type ToastType = 'info' | 'success' | 'error' | 'warning';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  addToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const contextValue = useMemo(() => ({ addToast }), [addToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {createPortal(
        <div className="fixed top-4 right-4 z-[9999] space-y-3">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`min-w-[240px] max-w-sm px-4 py-3 rounded-lg shadow-lg text-sm text-white flex items-start gap-3 ${
                toast.type === 'success'
                  ? 'bg-green-600'
                  : toast.type === 'error'
                  ? 'bg-red-600'
                  : toast.type === 'warning'
                  ? 'bg-amber-600'
                  : 'bg-gray-800'
              }`}
            >
              <i
                className={`ri-${
                  toast.type === 'success'
                    ? 'checkbox-circle-line'
                    : toast.type === 'error'
                    ? 'error-warning-line'
                    : toast.type === 'warning'
                    ? 'alert-line'
                    : 'information-line'
                } text-lg leading-none mt-0.5`}
              ></i>
              <span className="leading-snug">{toast.message}</span>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
